import type { SignoffRequirement } from './signoffRequirements'
import type { SignoffRecord, SignoffsField, SignoffSlot } from './signoffTypes'
import { EMPTY_SLOT_STATE } from './signoffTypes'

export type SignoffStatus = 'signed' | 'available' | 'locked'

export type SignoffLock = 'stage' | 'group' | 'role'

export interface SignoffRow {
  slot: SignoffSlot
  /** False when the ticket waives this slot; it stays signable either way. */
  required: boolean
  /** The live signature, when the slot is currently signed. */
  record?: SignoffRecord
  /** The last signature of a slot that was reset; absent if never signed. */
  previous?: SignoffRecord
  status: SignoffStatus
  lock?: SignoffLock
}

/**
 * Resolves every requirement into a display row — a slot the viewer cannot
 * sign still shows its status, so no row is ever dropped.
 *
 * The lock checks run widest-cause-first so each row names the real reason: a
 * closed-out or untagged ticket must not read as the viewer's roles.
 */
export function resolveSignoffRows({
  requirements,
  stageId,
  enabledStages,
  groupId,
  userHasRoles,
}: {
  requirements: ReadonlyArray<SignoffRequirement>
  stageId: string | undefined
  enabledStages: ReadonlyArray<string>
  groupId: string | undefined
  userHasRoles: (roles: ReadonlyArray<string>) => boolean
}): ReadonlyArray<SignoffRow> {
  const stageAllowsSigning =
    stageId !== undefined && enabledStages.includes(stageId)

  return requirements.map(
    ({ slot, required, record, previous }): SignoffRow => {
      if (record) return { slot, required, record, status: 'signed' }

      const base = { slot, required, previous }
      if (!stageAllowsSigning) {
        return { ...base, status: 'locked', lock: 'stage' }
      }
      if (slot.roles.length > 0 && !groupId) {
        return { ...base, status: 'locked', lock: 'group' }
      }
      if (!userHasRoles(slot.roles)) {
        return { ...base, status: 'locked', lock: 'role' }
      }
      return { ...base, status: 'available' }
    }
  )
}

/**
 * `record` appended to one slot and that slot marked active; every other slot
 * carries through untouched. Appending is what keeps a slot's history across
 * sign → reset → re-sign cycles.
 *
 * Counterpart of `deactivateSignoffs`; between them they are the only writers
 * of sign-off state.
 */
export function applySignature(
  signoffs: SignoffsField,
  slotId: string,
  record: SignoffRecord
): SignoffsField {
  const state = signoffs[slotId] ?? EMPTY_SLOT_STATE

  return {
    ...signoffs,
    [slotId]: {
      signatures: [...state.signatures, record],
      active: true,
    },
  }
}
